import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function isTrue(v) {
  if (v === true || v === 1) return true;
  if (v === false || v == null) return false;
  const s = String(v).toLowerCase();
  return s === "true" || s === "1";
}

/**
 * Matches FluVaccination.jsx fetch logic.
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseFluVaccinationFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.vaccination) {
    return result.vaccination;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildFluVaccinationPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(record.employeeName || record.applicantName || "").trim(),
  );
  const position = escapeHtml(String(record.positionAppliedFor || ""));

  const already = isTrue(record.alreadyReceivedVaccination);
  const request = isTrue(record.requestVaccinationHomeHealth);
  const refuse = isTrue(record.refuseVaccination);

  let reasonLabel = "—";
  if (refuse) reasonLabel = "I refuse the flu vaccination.";
  else if (request)
    reasonLabel = "I am requesting the flu vaccination from Home Health.";
  else if (already)
    reasonLabel =
      "I have already received the flu vaccination. (Proof may be required.)";

  const showConsentBlock = already || request;

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const displayName = employeeName || applicantSignedBy || "—";

  const refusalBlock = refuse
    ? `<div class="sec sec-warn">
      <div class="sec-h">Vaccination refusal</div>
      <div class="pad">
        <div class="fl">Reason for refusal</div>
        <div class="fv multiline">${escapeHtml(String(record.refusalReason || "")) || "—"}</div>
        <div class="mt">
          <div class="fl">Refusal acknowledgment signature</div>
          <div class="sig-line">${signatureBlock(record.refusalAcknowledgmentSignature)}</div>
          <div class="fl mt">Date</div>
          <div class="fv">${escapeHtml(formatDate(record.refusalAcknowledgmentDate)) || "—"}</div>
        </div>
      </div>
    </div>`
    : "";

  const consentBlock = showConsentBlock
    ? `<div class="sec sec-info">
      <div class="sec-h">Vaccination details (clinician)</div>
      <div class="pad grid2">
        <div><div class="fl">Clinician name</div><div class="fv">${escapeHtml(String(record.clinicianName || "")) || "—"}</div></div>
        <div><div class="fl">Clinician title</div><div class="fv">${escapeHtml(String(record.clinicianTitle || "")) || "—"}</div></div>
        <div><div class="fl">Location providing vaccine</div><div class="fv">${escapeHtml(String(record.locationProvidingVaccine || "")) || "—"}</div></div>
        <div><div class="fl">Site of injection</div><div class="fv">${escapeHtml(String(record.siteOfInjection || "")) || "—"}</div></div>
        <div><div class="fl">Administered by (type)</div><div class="fv">${escapeHtml(String(record.administeredByType || "")) || "—"}</div></div>
        <div><div class="fl">Administered by (name)</div><div class="fv">${escapeHtml(String(record.administeredByName || "")) || "—"}</div></div>
        <div><div class="fl">Lot number</div><div class="fv">${escapeHtml(String(record.lotNumber || "")) || "—"}</div></div>
        <div><div class="fl">Expiration date</div><div class="fv">${escapeHtml(formatDate(record.expirationDate)) || "—"}</div></div>
        <div class="full"><div class="fl">Print name (consent)</div><div class="fv">${escapeHtml(String(record.fluVaccinationConsentPrintName || "")) || "—"}</div></div>
        <div class="full"><div class="fl">Clinician signature</div><div class="sig-line">${signatureBlock(record.clinicianSignature)}</div></div>
      </div>
    </div>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Flu vaccination — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body { padding: 26px 16px 36px; background: transparent; min-height: 100vh; }
      .sheet {
        background: #fff;
        padding: 28px 30px 34px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 24px 48px -16px rgba(15,23,42,0.2);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet { padding: 0 !important; border: none !important; box-shadow: none !important; }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 12px;
    }
    .logo { height: 50px; width: auto; max-width: 190px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; margin: 0 0 3px 0; }
    .title-sub { font-size: 11px; font-weight: 500; color: #2563eb; margin: 0; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .fl { font-size: 8px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 3px; }
    .fv {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px 8px;
      min-height: 30px;
      font-size: 10px;
    }
    .fv.multiline { min-height: 56px; white-space: pre-wrap; }
    .sec { margin-bottom: 12px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .sec-warn { border-color: #fecaca; }
    .sec-info { border-color: #bfdbfe; }
    .sec-h {
      background: #f8fafc;
      padding: 8px 10px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      border-bottom: 1px solid #e2e8f0;
    }
    .sec-warn .sec-h { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
    .sec-info .sec-h { background: #eff6ff; color: #1e3a8a; border-color: #bfdbfe; }
    .pad { padding: 10px; }
    .grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 12px;
    }
    .grid2 .full { grid-column: 1 / -1; }
    .mt { margin-top: 8px; }
    .choice {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      background: #f9fafb;
      font-size: 10px;
      font-weight: 600;
      color: #0f172a;
    }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 14px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 11px; font-weight: 700; margin: 0 0 8px 0; }
    .sig-label { font-size: 8px; font-weight: 600; color: #6b7280; margin: 6px 0 3px 0; }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 26px;
      font-size: 10px;
      padding: 3px 0;
    }
    .sig-img { max-height: 40px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 8px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Flu vaccination consent / declination</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="meta">
      <div><div class="fl">Employee name</div><div class="fv">${employeeName || "—"}</div></div>
      <div><div class="fl">Position applied for</div><div class="fv">${position || "—"}</div></div>
    </div>

    <div class="sec">
      <div class="sec-h">Reason for submission</div>
      <div class="pad"><div class="choice">${escapeHtml(reasonLabel)}</div></div>
    </div>

    ${refusalBlock}
    ${consentBlock}

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
